import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { Combobox, ETypeOfNotification, InputTextCustom, Modal, setNotification } from 'medere-ui-framework';
import { ComboDataDTO } from '../../dto/ComboDataDTO';
import { FinancialAccountDTO } from '../../dto/FinancialAccountDTO';
import {
    CashRegisterDTO,
    CashRegisterFinancialAccountDTO,
    SaveCashRegisterRequestDTO,
} from '../../dto/locationcash/LocationCashDTO';
import {
    associateCashRegisterFinancialAccount,
    createCashRegister,
    disassociateCashRegisterFinancialAccount,
    getCashRegisterFinancialAccounts,
    getCashRegistersForAdministration,
    setDefaultCashRegisterFinancialAccount,
    updateCashRegister,
} from '../../services/LocationCashController';
import { getAllFinancialAccounts } from '../../services/FinancialAccountController';

import { UsersListItemDTO } from '../../dto/users/UsersDTO';

import './CashRegisterView.scss';
import { getUsersList } from '../../servicesHooks/Users/useUsersService';

function emptyRequest(): SaveCashRegisterRequestDTO {
    return { description: '', posId: null, active: true, financialAccountId: null, allowsTakeover: true, allowsSummary: false };
}

export default function CashRegisterView() {
    const dispatch = useDispatch();
    const [registers, setRegisters] = useState<CashRegisterDTO[]>([]);
    const [financialAccounts, setFinancialAccounts] = useState<FinancialAccountDTO[]>([]);
    const [allUsers, setAllUsers] = useState<UsersListItemDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const [editorVisible, setEditorVisible] = useState(false);
    const [editing, setEditing] = useState<CashRegisterDTO | null>(null);
    const [form, setForm] = useState<SaveCashRegisterRequestDTO>(emptyRequest());
    const [selectedPOS, setSelectedPOS] = useState<ComboDataDTO | null>(null);
    const [selectedFinancialAccountIds, setSelectedFinancialAccountIds] = useState<number[]>([]);
    const [defaultFinancialAccountId, setDefaultFinancialAccountId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [accountsRegister, setAccountsRegister] = useState<CashRegisterDTO | null>(null);
    const [associations, setAssociations] = useState<CashRegisterFinancialAccountDTO[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
    const [loadingAssociations, setLoadingAssociations] = useState(false);
    const [confirmingRemovalId, setConfirmingRemovalId] = useState<number | null>(null);
    const [selectedExclusiveUserIds, setSelectedExclusiveUserIds] = useState<number[]>([]);
    const [editingAssociatedAccounts, setEditingAssociatedAccounts] = useState<CashRegisterFinancialAccountDTO[]>([]);
    const [loadingEditAccounts, setLoadingEditAccounts] = useState(false);
    const [editDefaultAccountId, setEditDefaultAccountId] = useState<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([getCashRegistersForAdministration(), getAllFinancialAccounts(), getUsersList(0, 500)])
            .then(([cashRegisters, accounts, users]) => {
                if (!cancelled) {
                    setRegisters(cashRegisters);
                    setFinancialAccounts(accounts);
                    setAllUsers(users);
                }
            })
            .catch(() => dispatch(setNotification({
                title: 'No se pudieron cargar las cajas',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            })))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [dispatch, refresh]);

    function openCreate() {
        setEditing(null);
        setForm(emptyRequest());
        setSelectedPOS(null);
        setSelectedFinancialAccountIds([]);
        setDefaultFinancialAccountId(null);
        setSelectedExclusiveUserIds([]);
        setEditingAssociatedAccounts([]);
        setEditDefaultAccountId(null);
        setLoadingEditAccounts(false);
        setEditorVisible(true);
    }

    function openEdit(register: CashRegisterDTO) {
        setEditing(register);
        setForm({
            description: register.description ?? '',
            posId: register.posId ?? null,
            active: register.active,
            financialAccountId: register.financialAccountId ?? null,
            allowsTakeover: register.allowsTakeover ?? true,
            allowsSummary: register.allowsSummary ?? false,
        });
        setSelectedPOS(register.posId ? { id: register.posId, name: register.posDescription ?? `POS ${register.posNumber ?? ''}` } : null);
        setSelectedFinancialAccountIds([]);
        setDefaultFinancialAccountId(register.financialAccountId ?? null);
        setSelectedExclusiveUserIds(register.exclusiveUsers?.map(u => u.userId) ?? []);
        setEditingAssociatedAccounts([]);
        setEditDefaultAccountId(register.financialAccountId ?? null);
        setLoadingEditAccounts(true);
        setEditorVisible(true);
        getCashRegisterFinancialAccounts(register.locationCashRegisterId)
            .then(accounts => {
                const activeAccounts = accounts.filter(account => account.active);
                setEditingAssociatedAccounts(activeAccounts);

                // The row can be stale or may not contain the default after a
                // previous association update. The associations endpoint is
                // the authoritative source for this value.
                const defaultAccount = activeAccounts.find(account => account.isDefault);
                if (defaultAccount) {
                    setEditDefaultAccountId(defaultAccount.financialAccountId);
                }
            })
            .catch(() => setEditingAssociatedAccounts([]))
            .finally(() => setLoadingEditAccounts(false));
    }

    function handleCreationAccountsChange(event: MultiSelectChangeEvent) {
        const accountIds = (event.value ?? []) as number[];
        setSelectedFinancialAccountIds(accountIds);
        if (defaultFinancialAccountId != null && !accountIds.includes(defaultFinancialAccountId)) {
            setDefaultFinancialAccountId(accountIds[0] ?? null);
        }
    }

    async function saveRegister() {
        if (!form.description.trim()) {
            dispatch(setNotification({ title: 'Nombre requerido', desc: 'Ingrese una descripción para la caja', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
            return;
        }
        const activeSelectedAccountIds = selectedFinancialAccountIds.filter(accountId => financialAccounts.some(account => account.active && account.financialAccount === accountId));
        if (editing) {
            if (editingAssociatedAccounts.length === 0) {
                dispatch(setNotification({ title: 'Cuenta financiera requerida', desc: 'La caja debe tener al menos una cuenta financiera asociada.', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                return;
            }
            if (editDefaultAccountId == null) {
                dispatch(setNotification({ title: 'Cuenta predeterminada requerida', desc: 'Debe seleccionar una cuenta financiera predeterminada.', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                return;
            }
        } else {
            if (activeSelectedAccountIds.length === 0) {
                dispatch(setNotification({ title: 'Cuenta financiera requerida', desc: 'Debe asociar al menos una cuenta financiera a la caja.', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                return;
            }
            if (defaultFinancialAccountId == null) {
                dispatch(setNotification({ title: 'Cuenta predeterminada requerida', desc: 'Debe seleccionar una cuenta financiera predeterminada.', icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                return;
            }
        }
        setSaving(true);
        try {
            const selectedAccountIds = activeSelectedAccountIds;
            const selectedDefaultId = editing
                ? editDefaultAccountId
                : defaultFinancialAccountId ?? selectedAccountIds[0] ?? null;
            const body = {
                ...form,
                description: form.description.trim(),
                posId: selectedPOS?.id ?? null,
                financialAccountId: selectedDefaultId,
                financialAccountIds: editing ? undefined : selectedAccountIds,
                exclusiveUserIds: selectedExclusiveUserIds,
            };
            if (editing) await updateCashRegister(editing.locationCashRegisterId, body);
            else await createCashRegister(body);
            dispatch(setNotification({ title: editing ? 'Caja actualizada' : 'Caja creada', icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }));
            setEditorVisible(false);
            setRefresh(value => value + 1);
        } catch (error: any) {
            dispatch(setNotification({ title: 'No se pudo guardar la caja', desc: error?.response?.data?.message, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        } finally { setSaving(false); }
    }

    async function openAccounts(register: CashRegisterDTO) {
        setAccountsRegister(register);
        setSelectedAccountId(null);
        setConfirmingRemovalId(null);
        setLoadingAssociations(true);
        try { setAssociations(await getCashRegisterFinancialAccounts(register.locationCashRegisterId)); }
        catch { setAssociations([]); }
        finally { setLoadingAssociations(false); }
    }

    async function addAccount() {
        if (!accountsRegister || selectedAccountId == null) return;
        try {
            await associateCashRegisterFinancialAccount(accountsRegister.locationCashRegisterId, selectedAccountId, associations.length === 0);
            setAssociations(await getCashRegisterFinancialAccounts(accountsRegister.locationCashRegisterId));
            setSelectedAccountId(null);
            setRefresh(value => value + 1);
        } catch (error: any) {
            dispatch(setNotification({ title: 'No se pudo asociar la cuenta', desc: error?.response?.data?.message, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        }
    }

    async function removeAccount(account: CashRegisterFinancialAccountDTO) {
        if (!accountsRegister) return;
        try {
            await disassociateCashRegisterFinancialAccount(accountsRegister.locationCashRegisterId, account.financialAccountId);
            setAssociations(await getCashRegisterFinancialAccounts(accountsRegister.locationCashRegisterId));
        } catch (error: any) {
            dispatch(setNotification({ title: 'No se pudo desasociar la cuenta', desc: error?.response?.data?.message, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        }
    }

    async function makeDefault(account: CashRegisterFinancialAccountDTO) {
        if (!accountsRegister) return;
        try {
            await setDefaultCashRegisterFinancialAccount(accountsRegister.locationCashRegisterId, account.financialAccountId);
            setAssociations(await getCashRegisterFinancialAccounts(accountsRegister.locationCashRegisterId));
            setRefresh(value => value + 1);
        } catch (error: any) {
            dispatch(setNotification({ title: 'No se pudo cambiar la cuenta predeterminada', desc: error?.response?.data?.message, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
        }
    }

    const availableAccounts = financialAccounts.filter(account => account.active && !associations.some(a => a.financialAccountId === account.financialAccount));
    const accountOptionsForCreation = financialAccounts.filter(account => account.active);
    const selectedAccountsForCreation = accountOptionsForCreation.filter(account => selectedFinancialAccountIds.includes(account.financialAccount));

    const assignedRegisters = registers.filter(register => register.locationDescription).length;
    const activeRegisters = registers.filter(register => register.active).length;

    const renderRegisterCell = (row: CashRegisterDTO) => (
        <div className="cash-register-name-cell">
            <span className="cash-register-cell-icon" aria-hidden="true"><i className="pi pi-wallet" /></span>
            <div>
                <span className="cash-register-cell-primary">{row.description}</span>
                <span className="cash-register-cell-secondary">Caja #{row.locationCashRegisterId}</span>
            </div>
        </div>
    );

    const renderRelatedCell = (icon: string, value: string | null | undefined, emptyLabel: string) => (
        <div className="cash-register-related-cell">
            <i className={`pi ${icon}`} aria-hidden="true" />
            <span className={value ? '' : 'cash-register-muted'}>{value ?? emptyLabel}</span>
        </div>
    );

    return (
        <div className="cash-register-view">
            <header className="cash-register-page-header">
                <div className="cash-register-page-heading">
                    <span className="cash-register-page-icon" aria-hidden="true"><i className="pi pi-wallet" /></span>
                    <div>
                        <p className="cash-register-eyebrow">Configuración financiera</p>
                        <h1>Cajas</h1>
                        <p>Administre las cajas, sus cuentas financieras y la disponibilidad por puesto de atención.</p>
                    </div>
                </div>
                <Button label="Nueva caja" icon="pi pi-plus" className="buttonMain cash-register-primary-action" onClick={openCreate} />
            </header>

            <section className="cash-register-summary" aria-label="Resumen de cajas">
                <div className="cash-register-summary-card">
                    <span className="cash-register-summary-icon" aria-hidden="true"><i className="pi pi-wallet" /></span>
                    <div><span className="cash-register-summary-label">Total de cajas</span><strong>{registers.length}</strong></div>
                </div>
                <div className="cash-register-summary-card">
                    <span className="cash-register-summary-icon cash-register-summary-icon--success" aria-hidden="true"><i className="pi pi-check-circle" /></span>
                    <div><span className="cash-register-summary-label">Cajas activas</span><strong>{activeRegisters}</strong></div>
                </div>
                <div className="cash-register-summary-card">
                    <span className="cash-register-summary-icon cash-register-summary-icon--info" aria-hidden="true"><i className="pi pi-map-marker" /></span>
                    <div><span className="cash-register-summary-label">Asignadas a puestos</span><strong>{assignedRegisters}</strong></div>
                </div>
            </section>

            <section className="cash-register-panel" aria-labelledby="cash-register-list-title">
                <div className="cash-register-panel-header">
                    <div>
                        <h2 id="cash-register-list-title">Cajas configuradas</h2>
                        <p>La asignación a un puesto de atención se realiza desde la configuración de locaciones.</p>
                    </div>
                    <span className="cash-register-count"><i className="pi pi-list" aria-hidden="true" /> {registers.length} registros</span>
                </div>

                <DataTable
                    value={registers}
                    loading={loading}
                    stripedRows
                    paginator
                    rows={10}
                    className="cash-register-table"
                    emptyMessage="No hay cajas configuradas."
                >
                    <Column header="Caja" body={renderRegisterCell} />
                    <Column header="Puesto de atención" body={(row: CashRegisterDTO) => renderRelatedCell('pi-map-marker', row.locationDescription, 'Sin asignar')} />
                    <Column header="Punto de venta" body={(row: CashRegisterDTO) => renderRelatedCell('pi-tag', row.posDescription, 'Sin asignar')} />
                    <Column header="Cuenta predeterminada" body={(row: CashRegisterDTO) => renderRelatedCell('pi-credit-card', row.financialAccountName, 'Sin cuenta')} />
                    <Column header="Estado" body={(row: CashRegisterDTO) => <span className={`cash-register-status cash-register-status--${row.active ? 'active' : 'inactive'}`}><i className={`pi ${row.active ? 'pi-check-circle' : 'pi-minus-circle'}`} aria-hidden="true" />{row.active ? 'Activa' : 'Inactiva'}</span>} />
                    <Column header="Acciones" body={(row: CashRegisterDTO) => (
                        <div className="cash-register-row-actions">
                            <Button icon="pi pi-pencil" text rounded className="cash-register-icon-button" onClick={() => openEdit(row)} tooltip="Editar caja" tooltipOptions={{ position: 'top' }} aria-label={`Editar ${row.description}`} />
                            <Button icon="pi pi-credit-card" text rounded className="cash-register-icon-button" onClick={() => openAccounts(row)} tooltip="Gestionar cuentas financieras" tooltipOptions={{ position: 'top' }} aria-label={`Gestionar cuentas de ${row.description}`} />
                        </div>
                    )} />
                </DataTable>
            </section>

            <Modal
                visible={editorVisible}
                setVisible={setEditorVisible}
                header={<div className="cash-register-modal-title"><span className="cash-register-modal-title-icon" aria-hidden="true"><i className="pi pi-wallet" /></span><div><span className="cash-register-modal-eyebrow">Configuración de caja</span><h2>{editing ? 'Editar caja' : 'Nueva caja'}</h2></div></div>}
                width="w-medium"
                closable={!saving}
                className="cash-register-modal"
            >
                <div className="cash-register-modal-content">
                    <div className="cash-register-form-section">
                        <div className="cash-register-section-heading"><h3>Datos principales</h3><p>Defina la identificación de la caja y sus cuentas financieras.</p></div>
                        <div className="cash-register-form-grid">
                            <div className="cash-register-form-field cash-register-form-field--full">
                                <InputTextCustom label="Descripción" className="w-full" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                            </div>
                            {/* <div className="cash-register-form-field cash-register-form-field--full">
                                <Combobox label="Punto de venta" placeholder="Seleccionar punto de venta..." getItems={loadAllPOS} params={{ adminformtype: 0 }} value={selectedPOS} setValue={(value: ComboDataDTO | null) => setSelectedPOS(value)} optionLabel="name" />
                                <span className="cash-register-field-hint"><i className="pi pi-info-circle" aria-hidden="true" /> Opcional. Puede asignarse o modificarse luego.</span>
                            </div> */}
                            <div className="cash-register-form-field cash-register-form-field--full">
                                {editing ? (
                                    <>
                                        <label htmlFor="cash-register-default-account">Cuenta financiera predeterminada</label>
                                        <Dropdown
                                            inputId="cash-register-default-account"
                                            className="w-full"
                                            value={editDefaultAccountId}
                                            options={editingAssociatedAccounts}
                                            optionLabel="financialAccountName"
                                            optionValue="financialAccountId"
                                            placeholder={loadingEditAccounts ? 'Cargando cuentas...' : 'Sin cuenta predeterminada'}
                                            disabled={loadingEditAccounts}
                                            onChange={event => setEditDefaultAccountId(event.value ?? null)}
                                        />
                                        <span className="cash-register-field-hint"><i className="pi pi-info-circle" aria-hidden="true" /> Para administrar todas las cuentas asociadas, utilizar “Gestionar cuentas financieras".</span>
                                    </>
                                ) : (
                                    <>
                                        <label htmlFor="cash-register-associated-accounts">Cuentas financieras asociadas</label>
                                        <MultiSelect inputId="cash-register-associated-accounts" className="w-full" value={selectedFinancialAccountIds} options={accountOptionsForCreation} optionLabel="name" optionValue="financialAccount" placeholder="Seleccionar cuentas financieras..." display="chip" onChange={handleCreationAccountsChange} />
                                        <span className="cash-register-field-hint"><i className="pi pi-info-circle" aria-hidden="true" /> Se pueden asociar varias cuentas desde el alta.</span>
                                    </>
                                )}
                            </div>
                            {!editing && <div className="cash-register-form-field cash-register-form-field--full">
                                <label htmlFor="cash-register-creation-default-account">Cuenta predeterminada</label>
                                <Dropdown inputId="cash-register-creation-default-account" className="w-full" value={defaultFinancialAccountId} options={selectedAccountsForCreation} optionLabel="name" optionValue="financialAccount" placeholder="Seleccionar cuenta predeterminada..." disabled={selectedAccountsForCreation.length === 0} onChange={event => setDefaultFinancialAccountId(event.value ?? null)} />
                                <span className="cash-register-field-hint"><i className="pi pi-star" aria-hidden="true" /> Obligatorio. Seleccione la cuenta que se usará por defecto.</span>
                            </div>}
                        </div>
                    </div>

                    <div className="cash-register-active-control">
                        <div><span className="cash-register-active-title">Disponibilidad</span><span className="cash-register-field-hint">Las cajas inactivas no estarán disponibles para operar.</span></div>
                        <div className="cash-register-active-toggle"><InputSwitch inputId="cash-register-active" checked={form.active} onChange={e => setForm({ ...form, active: e.value })} /><label htmlFor="cash-register-active">{form.active ? 'Activa' : 'Inactiva'}</label></div>
                    </div>

                    <div className="cash-register-form-section">
                        <div className="cash-register-section-heading"><h3>Permisos</h3><p>Configure el comportamiento del puesto asociado a esta caja.</p></div>
                        <div className="flex flex-column gap-3">
                            <div className="cash-register-active-control">
                                <div><span className="cash-register-active-title">Permitir toma de puesto</span><span className="cash-register-field-hint">Si está activo, otros usuarios pueden tomar el puesto cuando está ocupado.</span></div>
                                <div className="cash-register-active-toggle"><InputSwitch inputId="cash-register-takeover" checked={form.allowsTakeover ?? true} onChange={e => setForm({ ...form, allowsTakeover: e.value })} /><label htmlFor="cash-register-takeover">{form.allowsTakeover ? 'Sí' : 'No'}</label></div>
                            </div>
                            <div className="cash-register-form-field cash-register-form-field--full">
                                <label htmlFor="cash-register-exclusive-users">Usuarios exclusivos</label>
                                <MultiSelect
                                    inputId="cash-register-exclusive-users"
                                    className="w-full"
                                    value={selectedExclusiveUserIds}
                                    options={allUsers}
                                    optionLabel="username"
                                    optionValue="userId"
                                    placeholder="Sin restricción (todos los usuarios)"
                                    display="chip"
                                    filter
                                    filterBy="username,firstname,lastname"
                                    onChange={e => setSelectedExclusiveUserIds(e.value ?? [])}
                                />
                                <span className="cash-register-field-hint"><i className="pi pi-info-circle" aria-hidden="true" /> Si no se selecciona ningún usuario, cualquier usuario puede operar la caja.</span>
                            </div>
                        </div>
                    </div>

                    <div className="cash-register-modal-actions">
                        <Button label="Cancelar" className="buttonMain3" onClick={() => setEditorVisible(false)} disabled={saving} />
                        <Button label={editing ? 'Guardar cambios' : 'Crear caja'} icon="pi pi-check" className="buttonMain" loading={saving} onClick={saveRegister} disabled={!form.description.trim() || (editing !== null && loadingEditAccounts) || (editing !== null ? editingAssociatedAccounts.length === 0 || editDefaultAccountId == null : selectedFinancialAccountIds.length === 0 || defaultFinancialAccountId == null)} />
                    </div>
                </div>
            </Modal>

            <Modal
                visible={accountsRegister !== null}
                setVisible={(value) => { if (!value) { setAccountsRegister(null); setConfirmingRemovalId(null); } }}
                header={<div className="cash-register-modal-title"><span className="cash-register-modal-title-icon" aria-hidden="true"><i className="pi pi-credit-card" /></span><div><span className="cash-register-modal-eyebrow">Cuentas financieras</span><h2>{accountsRegister?.description ?? ''}</h2></div></div>}
                width="w-large"
                className="cash-register-modal"
            >
                <div className="cash-register-modal-content">
                    <div className="cash-register-modal-intro"><i className="pi pi-info-circle" aria-hidden="true" /><p>Asocie las cuentas que esta caja puede utilizar. Una cuenta debe quedar marcada como predeterminada.</p></div>
                    <section className="cash-register-association-adder" aria-labelledby="cash-register-association-title">
                        <div className="cash-register-section-heading"><h3 id="cash-register-association-title">Agregar cuenta</h3><p>Solo se muestran cuentas financieras activas aún no asociadas.</p></div>
                        <div className="cash-register-association-controls">
                            <Dropdown inputId="cash-register-financial-account" className="w-full" value={selectedAccountId} options={availableAccounts} optionLabel="name" optionValue="financialAccount" placeholder="Seleccionar cuenta financiera" onChange={e => setSelectedAccountId(e.value)} />
                            <Button label="Asociar" icon="pi pi-plus" className="buttonMain" onClick={addAccount} disabled={selectedAccountId == null} />
                        </div>
                    </section>

                    <section className="cash-register-association-list" aria-labelledby="cash-register-associated-title">
                        <div className="cash-register-list-heading"><h3 id="cash-register-associated-title">Cuentas asociadas</h3><span>{associations.length}</span></div>
                        <DataTable value={associations} loading={loadingAssociations} className="cash-register-table cash-register-association-table" emptyMessage="No hay cuentas asociadas.">
                            <Column header="Cuenta" body={(row: CashRegisterFinancialAccountDTO) => <div className="cash-register-account-cell"><span className="cash-register-cell-icon cash-register-cell-icon--small" aria-hidden="true"><i className="pi pi-credit-card" /></span><span>{row.financialAccountName}</span></div>} />
                            <Column field="currency" header="Moneda" body={(row: CashRegisterFinancialAccountDTO) => <span className="cash-register-currency">{row.currency}</span>} />
                            <Column header="Estado" body={(row: CashRegisterFinancialAccountDTO) => <span className={`cash-register-default-badge${row.isDefault ? ' cash-register-default-badge--active' : ''}`}><i className={`pi ${row.isDefault ? 'pi-star-fill' : 'pi-link'}`} aria-hidden="true" />{row.isDefault ? 'Predeterminada' : 'Asociada'}</span>} />
                            <Column header="Acciones" body={(row: CashRegisterFinancialAccountDTO) => (
                                <div className="cash-register-row-actions cash-register-row-actions--association">
                                    {!row.isDefault && <Button label="Marcar predeterminada" text size="small" className="cash-register-default-button" onClick={() => makeDefault(row)} />}
                                    {confirmingRemovalId === row.financialAccountId ? <><Button label="¿Quitar?" text size="small" className="cash-register-confirm-button" onClick={() => { setConfirmingRemovalId(null); void removeAccount(row); }} /><Button icon="pi pi-times" text rounded className="cash-register-icon-button" onClick={() => setConfirmingRemovalId(null)} aria-label="Cancelar quitar cuenta" /></> : <Button icon="pi pi-trash" text rounded className="cash-register-icon-button" onClick={() => setConfirmingRemovalId(row.financialAccountId)} tooltip="Quitar asociación" tooltipOptions={{ position: 'top' }} aria-label={`Quitar ${row.financialAccountName}`} />}
                                </div>
                            )} />
                        </DataTable>
                    </section>
                </div>
            </Modal>
        </div>
    );
}
